import type { RiskEntry, SrmModule } from '@types';

/**
 * Extracts actionable risk entries from all supported Gemini analysis result types.
 * Every risk is stamped with source: 'module_analysis' and status: 'Pending'.
 * The user MUST Accept or Discard each one in the Risk Register.
 */
export const extractRisksFromIntelligence = (moduleId: string, resultData: any): RiskEntry[] => {
    const risks: RiskEntry[] = [];
    const payload = resultData?.result || resultData?.response || resultData?.analysis || resultData;
    const activeData = payload?.fullReportData || payload;
    if (!activeData) return risks;

    // ── Helpers ────────────────────────────────────────────────────────────────
    const rng = () => Math.floor(Math.random() * 90000) + 10000;

    const scaleMap: Record<string, number> = {
        critical: 5, extreme: 5,
        high: 4,    major: 4,   severe: 4,
        medium: 3,  moderate: 3, significant: 3,
        low: 2,     minor: 2,
        minimal: 1, negligible: 1,
    };
    const toScale = (val: string | number): number => typeof val === 'number' && Number.isInteger(val) && val >= 1 && val <= 5
        ? val : scaleMap[String(val || '').toLowerCase().trim()] ?? 2;
    const score = (l: string | number, i: string | number) => toScale(l) * toScale(i);

    /** Normalise a raw category string to a known RiskEntry category. */
    const normaliseCategory = (raw: string): RiskEntry['category'] => {
        const r = (raw || '').toLowerCase();
        if (r.includes('infra') || r.includes('asset') || r.includes('facility')) return 'Infrastructure';
        if (r.includes('geo') || r.includes('political')) return 'Geopolitical';
        if (r.includes('terror') || r.includes('security') || r.includes('crime')) return 'Security';
        if (r.includes('conflict') || r.includes('war') || r.includes('armed')) return 'Conflict';
        if (r.includes('natural') || r.includes('disaster') || r.includes('catastroph')) return 'Natural Disaster';
        if (r.includes('health') || r.includes('epidemic') || r.includes('pandemic')) return 'Health';
        if (r.includes('cyber') || r.includes('digital') || r.includes('hack')) return 'Cyber';
        if (r.includes('people') || r.includes('personnel') || r.includes('human')) return 'People';
        if (r.includes('intel') || r.includes('signal') || r.includes('intel')) return 'Intelligence';
        if (r.includes('oper') || r.includes('logist') || r.includes('supply')) return 'Operational';
        return 'Security';
    };

    // ── A. Facility & Asset Protection (FAPP / FSAT) ────────────────────────
    const fappThreats = activeData.threatAssessment?.specificThreats || activeData.threatAssessment || activeData.criticalInfrastructureVulnerabilities || [];
    if (Array.isArray(fappThreats)) {
        fappThreats.forEach((t: any) => {
            if (!t?.threat && !t?.vulnerability && !t?.label)
                return;
            const l = t.likelihood || t.probability || 'Medium';
            const im = t.impact || t.severity || 'High';
            risks.push({
                id: `R-FAPP-${rng()}`,
                threat: t.threat || t.vulnerability || t.label || 'Facility Threat',
                category: 'Infrastructure',
                likelihood: l,
                impact: im,
                score: score(l, im),
                mitigation: t.mitigation || t.remediation || t.controlMeasure || 'Apply physical hardening.',
                owner: 'FAPP Engine',
                status: 'Pending',
                source: 'module_analysis',
                recommendation: 'Infrastructure Hardening',
            } as RiskEntry);
        });
    }

    // ── B. EP Threat Analysis ────────────────────────────────────────────────
    const epThreats = activeData.keyThreats || activeData.threatsToPersonnel || [];
    if (Array.isArray(epThreats)) {
        epThreats.forEach((t: any) => {
            if (!t?.threat && !t?.name)
                return;
            risks.push({
                id: `R-EP-${rng()}`,
                threat: t.threat || t.name || 'EP Threat',
                category: 'People',
                likelihood: t.likelihood || 'Medium',
                impact: t.impact || 'High',
                score: score(t.likelihood || 'Medium', t.impact || 'High'),
                mitigation: t.mitigation || t.recommendation || 'Enhance protective detail protocols.',
                owner: 'EP Threat Engine',
                status: 'Pending',
                source: 'module_analysis',
                recommendation: 'Personnel Security Detail',
            } as RiskEntry);
        });
    }

    // ── C. Country / Geopolitical Analysis — riskFactors[] ──────────────────
    const riskFactors = activeData.riskFactors || [];
    if (Array.isArray(riskFactors) && activeData.reportType === 'GeopoliticalAnalysis') {
        riskFactors.filter((f: any) => f?.factor && (f.score || 0) >= 4).forEach((f: any) => {
            const l = f.score >= 8 ? 'High' : 'Medium';
            const im = f.score >= 8 ? 'High' : 'Medium';
            risks.push({
                id: `R-GEO-RF-${rng()}`,
                threat: `Country Risk: ${f.factor}`,
                category: normaliseCategory(f.factor),
                likelihood: l,
                impact: im,
                score: score(l, im),
                mitigation: `Monitor ${f.factor} indicators (current score ${f.score}/10). Activate contingency plans as required.`,
                owner: 'Geopolitical Engine',
                status: 'Pending',
                source: 'module_analysis',
                recommendation: 'Geopolitical Analysis',
            } as RiskEntry);
        });
    }

    // ── D. Country / Geopolitical — threat groups ────────────────────────────
    const threatGroups = activeData.threatGroups || [];
    if (Array.isArray(threatGroups)) {
        threatGroups.slice(0, 3).forEach((g: any) => {
            if (!g?.name)
                return;
            risks.push({
                id: `R-TG-${rng()}`,
                threat: `Active Threat Actor: ${g.name}`,
                category: 'Security',
                likelihood: 'High',
                impact: 'High',
                score: score('High', 'High'),
                mitigation: g.summary || 'Monitor group activities and restrict personnel movement in stated AO.',
                owner: 'Geopolitical Engine',
                status: 'Pending',
                source: 'module_analysis',
                recommendation: 'Geopolitical Analysis',
            } as RiskEntry);
        });
    }

    // ── E. MDCOA / MLCOA Scenarios ───────────────────────────────────────────
    const mdcoa = activeData.scenarios?.mdcoa;
    if (mdcoa?.title) {
        risks.push({
            id: `R-MDCOA-${rng()}`,
            threat: `MDCOA: ${mdcoa.title}`,
            category: 'Geopolitical',
            likelihood: 'Low',
            impact: 'Critical',
            score: score('Low', 'Critical'),
            mitigation: mdcoa.description || 'Develop contingency and crisis response protocols.',
            owner: 'Scenario Engine',
            status: 'Pending',
            source: 'module_analysis',
            recommendation: 'Crisis Management Framework',
        } as RiskEntry);
    }

    // ── F. Regional Risks (composite / multi-country reports) ───────────────
    const regionalRisks = activeData.regionalRisks || [];
    if (Array.isArray(regionalRisks)) {
        regionalRisks.forEach((r: any) => {
            if (!r?.region)
                return;
            risks.push({
                id: `R-REG-${rng()}`,
                threat: `Regional Risk: ${r.region}`,
                category: 'Geopolitical',
                likelihood: r.riskLevel || 'Medium',
                impact: 'High',
                score: score(r.riskLevel || 'Medium', 'High'),
                mitigation: `Monitor region. Key drivers: ${(r.drivers || []).join(', ')}.`,
                owner: 'Geopolitical Engine',
                status: 'Pending',
                source: 'module_analysis',
                recommendation: 'Geopolitical Analysis',
            } as RiskEntry);
        });
    }

    // ── G. Route / Itinerary Risks ───────────────────────────────────────────
    const segments = activeData.segments || [];
    if (Array.isArray(segments)) {
        segments.forEach((s: any) => {
            (s.specificRisks || s.risks || []).forEach((riskStr: string) => {
                risks.push({
                    id: `R-RT-${rng()}`,
                    threat: `Route Risk (${s.legName || s.name || 'Segment'}): ${riskStr}`,
                    category: 'Operational',
                    likelihood: 'Medium',
                    impact: 'Medium',
                    score: score('Medium', 'Medium'),
                    mitigation: s.mitigation || s.recommendation || 'Enforce strict movement security protocols.',
                    owner: 'Route Risk Engine',
                    status: 'Pending',
                    source: 'module_analysis',
                    recommendation: 'Operational Security',
                } as RiskEntry);
            });
        });
    }

    // ── H. Security Incidents ────────────────────────────────────────────────
    const incidents = activeData.securityIncidents || activeData.recentEvents || [];
    if (Array.isArray(incidents)) {
        incidents.slice(0, 3).forEach((inc: any) => {
            if (!inc?.title && !inc?.type)
                return;
            risks.push({
                id: `R-INC-${rng()}`,
                threat: `Incident: ${inc.type || inc.title || 'Security Event'} — ${inc.location || inc.country || ''}`,
                category: 'Intelligence',
                likelihood: 'High',
                impact: 'Medium',
                score: score('High', 'Medium'),
                mitigation: inc.description || inc.summary || 'Ensure incident awareness and brief all personnel.',
                owner: 'Intelligence Engine',
                status: 'Pending',
                source: 'module_analysis',
                recommendation: 'Real-time Intelligence',
            } as RiskEntry);
        });
    }

    // ── I. HSSE Compliance Risks ─────────────────────────────────────────────
    const hsseGaps = activeData.complianceGaps || activeData.criticalFindings || [];
    if (Array.isArray(hsseGaps)) {
        hsseGaps.slice(0, 3).forEach((gap: any) => {
            if (!gap?.finding && !gap?.gap && !gap?.title)
                return;
            risks.push({
                id: `R-HSSE-${rng()}`,
                threat: `HSSE Gap: ${gap.finding || gap.gap || gap.title || 'Compliance Gap'}`,
                category: 'Operational',
                likelihood: gap.likelihood || 'Medium',
                impact: gap.severity || gap.impact || 'High',
                score: score(gap.likelihood || 'Medium', gap.severity || gap.impact || 'High'),
                mitigation: gap.recommendation || gap.action || 'Immediate remediation required per HSSE standards.',
                owner: 'HSSE Compliance Engine',
                status: 'Pending',
                source: 'module_analysis',
                recommendation: 'HSSE Compliance Suite',
            } as RiskEntry);
        });
    }

    // ── J. EP Ops — Tactical Threats ─────────────────────────────────────────
    const tacticalThreats = activeData.tacticalThreats || activeData.operationalRisks || [];
    if (Array.isArray(tacticalThreats)) {
        tacticalThreats.forEach((t: any) => {
            if (!t?.threat && !t?.risk && !t?.name)
                return;
            risks.push({
                id: `R-TAC-${rng()}`,
                threat: t.threat || t.risk || t.name || 'Tactical Threat',
                category: 'Security',
                likelihood: t.likelihood || 'High',
                impact: t.impact || 'High',
                score: score(t.likelihood || 'High', t.impact || 'High'),
                mitigation: t.mitigation || t.countermeasure || 'Deploy counter-measure protocols.',
                owner: 'EP Ops Engine',
                status: 'Pending',
                source: 'module_analysis',
                recommendation: 'EP Operations',
            } as RiskEntry);
        });
    }

    // Drop any entry with an empty threat string, deduplicate by title within this batch
    const seen = new Set<string>();
    return risks.filter(r => {
        if (!r.threat || seen.has(r.threat.toLowerCase())) return false;
        seen.add(r.threat.toLowerCase());
        return true;
    }).map(risk => ({ ...risk, targetComponentId: moduleId }));
};

export function stageSrmSetupRisks(modules: SrmModule[], existing: RiskEntry[] = []): RiskEntry[] {
    const risks: RiskEntry[] = existing.map(risk => ({ ...risk, status: 'Pending' as const }));
    const titles = new Set(risks.map(risk => risk.threat.toLowerCase().trim()));
    const createdAt = new Date().toISOString();
    for (const module of modules)
        for (const risk of extractRisksFromIntelligence(module.id, module.resultData)) {
            const title = risk.threat.toLowerCase().trim();
            if (!titles.has(title)) {
                risks.push({ ...risk, createdAt });
                titles.add(title);
            }
        }
    return risks;
}
